import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import { persistor, store } from './redux/store';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from './module/ThemeManager';
import { enableMapSet } from 'immer';
import { ChakraProvider } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { ClearCacheProvider } from 'react-clear-cache';
import mixpanel from 'mixpanel-browser';
import { MainLoading } from './components/Loading/MainLoading';
var { registerObserver } = require('react-perf-devtool');

if (process.env.REACT_APP_NODE_ENV === 'dev') {
    function callback(measures: any) {
        // do something with the measures
    }
    registerObserver({}, callback);
}
enableMapSet();

document.body.classList.remove('chakra-ui-light');
document.body.classList.add('chakra-ui-dark');

Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    debug: false,
    integrations: [
        new Sentry.BrowserTracing({
            tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
            routingInstrumentation: Sentry.reactRouterV6Instrumentation(
                React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            ),
        }),
        new Sentry.Replay(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN;
if (mixpanelToken) {
    mixpanel.init(mixpanelToken, {
        debug: process.env.NODE_ENV !== 'production',
        track_pageview: process.env.NODE_ENV === 'production',
        autotrack: process.env.NODE_ENV === 'production',
        cookie_name: 'thspian-x-cookie',
        secure_cookie: true,
        persistence: 'cookie',
        persistence_name: 'thspain_persist',
        disable_persistence: true,
    });
}

require('react-web-vector-icons/fonts');

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
    <HelmetProvider>
        <Helmet>
            <script type="application/ld+json">
                {`{
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "Thspian",
                    "url": "https://thspian.com",
                    "logo": "https://thspian.com/logo.png",
                    "about": ["content festivals", "creative competitions", "UGC campaigns", "auditions", "content creator", "creative funding", "earn money", "thespian"],
                    "sameAs": [
                        "https://www.instagram.com/thspian",
                        "https://www.tiktok.com/thspian"
                    ],
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "telephone": "+1-732-226-7040",
                        "contactType": "customer service",
                        "areaServed": "Worldwide",
                        "availableLanguage": ["English"]
                    }
                }`}
            </script>
            <script type="application/ld+json">
                {`{
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "url": "https://thspian.com",
                    "name": "Thspian",
                    "description": "Creative Competitions, Content Festivals & UGC Platform",
                    "potentialAction": {
                        "@type": "SearchAction",
                        "target": {
                            "@type": "EntryPoint",
                            "urlTemplate": "https://thspian.com/discover?searchTerm={search_term_string}"
                        },
                        "query-input": "required name=search_term_string"
                    }
                }`}
            </script>

            <title>Thspian – Creative Competitions, Content Festivals & UGC Platform</title>
            <meta
                name="description"
                content="Connect with a vast community of creators and participate in creative campaigns for renowned brands, all while earning rewards. Creative Competitions, Film Festivals & UGC Platform"
            />
            <link rel="canonical" href="https://thspian.com" />
        </Helmet>
        <ClearCacheProvider duration={60000} auto>
            <ChakraProvider resetCSS>
                <Provider store={store}>
                    <ThemeProvider>
                        <PersistGate loading={<MainLoading />} persistor={persistor}>
                            <App />
                        </PersistGate>
                    </ThemeProvider>
                </Provider>
            </ChakraProvider>
        </ClearCacheProvider>
    </HelmetProvider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
